import React, { useState, useMemo, useRef } from 'react';
import {
  FormControl, InputLabel, Select, MenuItem,
  Typography, Stack, TextField, Box, Divider,
  InputAdornment,
} from '@mui/material';
import {
  LocationOnOutlined as LocationIcon,
  SearchOutlined as SearchIcon,
  InboxOutlined as EmptyIcon,
} from '@mui/icons-material';

export default function LocationSelect({
  locations   = [],
  value       = '',
  onChange,
  label       = 'Location',
  placeholder = 'Select a location',
  disabled    = false,
  loading     = false,
  size        = 'small',
  fullWidth   = true,
  required    = false,
  error       = false,
  helperText  = null,
  name        = 'location_id',
}) {
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return locations;
    const q = search.toLowerCase();
    return locations.filter((loc) =>
      loc.name?.toLowerCase().includes(q) ||
      loc.city?.toLowerCase().includes(q) ||
      loc.state?.toLowerCase().includes(q)
    );
  }, [locations, search]);

  const handleClose = () => setSearch('');

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      required={required}
      error={error}
    >
      <InputLabel>{loading ? 'Loading locations…' : label}</InputLabel>
      <Select
        name={name}
        value={value}
        label={loading ? 'Loading locations…' : label}
        onChange={(e) => onChange?.(e.target.value, e)}
        onClose={handleClose}
        startAdornment={
          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
        }
        // Keeps the dropdown open while typing in the search field
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: { maxHeight: 320, borderRadius: 2 },
          },
        }}
        displayEmpty
      >

        {/* ── Search field ── */}
        <Box
          sx={{ px: 1.5, pt: 1, pb: 0.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
          // Prevent keydown from bubbling to Select (would close/navigate dropdown)
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            autoFocus
            placeholder="Search locations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 },
            }}
          />
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        {/* ── Placeholder option ── */}
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">{placeholder}</Typography>
        </MenuItem>

        {/* ── Filtered results ── */}
        {filtered.length === 0 ? (
          <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" color="text.disabled" display="block">
              No locations match "{search}"
            </Typography>
          </Box>
        ) : (
          filtered.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              <Stack>
                <Typography variant="body2">{loc.name ?? loc.location_name}</Typography>
                {(loc.city || loc.state) && ( 
                  <Typography variant="caption" color="text.secondary">
                    {loc.city ?? ''}{loc.state ? `, ${loc.state}` : ''}
                  </Typography>
                )}
              </Stack>
            </MenuItem>
          ))
        )}

        {/* ── Result count ── */}
        {search && filtered.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, borderTop: '0.5px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              {filtered.length} of {locations.length} location{locations.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Select>

      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
}